import { useState, useEffect } from 'react';
import { db } from '@classroom-ai/firebase-config';
import { query, where, collection, onSnapshot } from 'firebase/firestore';

import { agentTrpc } from 'src/lib/agent-trpc';

// ----------------------------------------------------------------------
// Per-class "relevant data" signals for the weekly grid (epic Phase 2):
//   • live sessions — real-time, from the Firestore `liveSessions` mirror
//   • pending reviews — one-shot per-class count from the agent (tRPC)
// Roster size is read per-class inside the block via the react-query
// `useGetClassRoster` hook (deduped by query key), not here.
//
// Both hooks take the full set of the teacher's placed classes and return a
// Map keyed by classId, so the grid can look up a class's signals in O(1).
// ----------------------------------------------------------------------

export type LiveCount = { live: number; escalated: number };

/** Stable, order-independent key for a set of class ids (effect dependency). */
function idsKey(ids: string[]): string {
  return [...ids].sort().join(',');
}

const FIRESTORE_IN_LIMIT = 30;

/**
 * Real-time count of live (non-ended) sessions per class, from `liveSessions`.
 * Mirrors the `onSnapshot` pattern in the Sessions view, but fans across every
 * class with a chunked `where('classId','in', …)` (Firestore caps `in` at 30).
 */
export function useLiveSessionCounts(classIds: string[]): Map<string, LiveCount> {
  const [counts, setCounts] = useState<Map<string, LiveCount>>(new Map());
  const key = idsKey(classIds);

  useEffect(() => {
    if (!classIds.length) {
      setCounts(new Map());
      return undefined;
    }

    const chunks: string[][] = [];
    for (let i = 0; i < classIds.length; i += FIRESTORE_IN_LIMIT) {
      chunks.push(classIds.slice(i, i + FIRESTORE_IN_LIMIT));
    }

    // Each chunk's latest docs; recomputed into a per-class map on any change.
    const docsByChunk = new Map<number, { classId: string; status?: string; escalated?: boolean }[]>();
    const recompute = () => {
      const m = new Map<string, LiveCount>();
      for (const docs of docsByChunk.values()) {
        for (const d of docs) {
          if (d.status === 'ENDED') continue;
          const cur = m.get(d.classId) ?? { live: 0, escalated: 0 };
          cur.live += 1;
          if (d.escalated) cur.escalated += 1;
          m.set(d.classId, cur);
        }
      }
      setCounts(m);
    };

    const unsubs = chunks.map((chunk, ci) => {
      const q = query(collection(db, 'liveSessions'), where('classId', 'in', chunk));
      return onSnapshot(
        q,
        (snap) => {
          docsByChunk.set(
            ci,
            snap.docs.map((d) => d.data() as { classId: string; status?: string; escalated?: boolean })
          );
          recompute();
        },
        (err) => console.error('[Week] liveSessions onSnapshot error:', err)
      );
    });

    return () => unsubs.forEach((u) => u());
    // key encodes the class-id set; classIds is intentionally reconstructed inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return counts;
}

/**
 * Pending-review count per class — one `assessment.getPendingReviews` call per
 * class (there's no batched/aggregate query). Counts change slowly, so this is a
 * one-shot fetch, not a live subscription. Classes that error resolve to 0.
 */
export function usePendingReviewCounts(classIds: string[]): Map<string, number> {
  const [counts, setCounts] = useState<Map<string, number>>(new Map());
  const key = idsKey(classIds);

  useEffect(() => {
    if (!classIds.length) {
      setCounts(new Map());
      return undefined;
    }

    let cancelled = false;
    Promise.all(
      classIds.map(async (id) => {
        try {
          const res = await agentTrpc.assessment.getPendingReviews(id);
          return [id, res.reviews.length] as const;
        } catch (err) {
          console.error('[Week] getPendingReviews failed for', id, err);
          return [id, 0] as const;
        }
      })
    ).then((entries) => {
      if (!cancelled) setCounts(new Map(entries));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return counts;
}
